import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import {
  esignApiService, type ESignRequestStatus, type ESignRequestSummary, type ESignStatus,
} from '../services/esignApiService'
import { ApiError } from '../services/BaseApiService'
import { Badge, Button, ConfirmModal, PageHeader, Textarea } from '../components/ui'
import {
  capitalize, downloadBlob, errorMessage, fmtDate, isPlanUpgradeError, REQUEST_STATUS_CHIP, SIGNER_STATUS_CHIP,
} from '../components/esign/esignShared'
import { useIsMobile } from '../hooks/useMediaQuery'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../styles/theme'

const FILTERS: { value: ESignRequestStatus | undefined; label: string }[] = [
  { value: undefined,   label: 'All' },
  { value: 'pending',   label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'declined',  label: 'Declined' },
  { value: 'voided',    label: 'Voided' },
  { value: 'expired',   label: 'Expired' },
]

const EVENT_LABELS: Record<string, string> = {
  created: 'Request created',
  sent: 'Invitation sent',
  viewed: 'Viewed',
  signed: 'Signed',
  declined: 'Declined',
  reminded: 'Reminder sent',
  voided: 'Voided',
  expired: 'Expired',
  completed: 'Completed',
}

const eventLabel = (t: string) => EVENT_LABELS[t.toLowerCase()] ?? capitalize(t.replace(/[_-]+/g, ' '))

function StatusBadge({ status, map }: { status: string; map: Record<string, { bg: string; text: string; border: string }> }) {
  const t = map[status] ?? { bg: '#EEF0F1', text: '#6B7180', border: '#EEF0F1' }
  return <Badge bg={t.bg} color={t.text} border={t.border}>{capitalize(status)}</Badge>
}

export default function ESignRequestsPage() {
  const isMobile = useIsMobile()
  const [filter, setFilter] = useState<ESignRequestStatus | undefined>(undefined)
  const [rows, setRows] = useState<ESignRequestSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [upgrade, setUpgrade] = useState(false)
  // ?request=<id> (the deep link in e-sign notifications and emails) opens that request's drawer.
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get('request')
  const setSelectedId = useCallback((id: string | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (id) next.set('request', id)
      else next.delete('request')
      return next
    }, { replace: true })
  }, [setSearchParams])

  const load = useCallback(async (status: ESignRequestStatus | undefined) => {
    setLoading(true)
    setError('')
    try {
      setRows(await esignApiService.list(status))
      setUpgrade(false)
    } catch (err) {
      if (isPlanUpgradeError(err)) setUpgrade(true)
      else setError(errorMessage(err, 'Failed to load signing requests.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(filter) }, [filter, load])

  const th: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontSize: fontSize.sm, fontWeight: 600,
    color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`, background: colors.background, whiteSpace: 'nowrap',
  }
  const td: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, color: colors.text, borderBottom: `1px solid ${colors.border}`,
  }

  return (
    <div>
      <PageHeader title="E-signatures" subtitle="Documents sent for signature from your render jobs." />

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: spacing.lg }}>
        {FILTERS.map(f => (
          <button key={f.label} onClick={() => setFilter(f.value)} aria-pressed={filter === f.value}
            style={{
              padding: '5px 12px', borderRadius: radius.full, fontSize: fontSize.sm, cursor: 'pointer',
              border: `1px solid ${filter === f.value ? colors.primary : colors.border}`,
              background: filter === f.value ? colors.primary : colors.surface,
              color: filter === f.value ? colors.onPrimary : colors.textSecondary,
              fontWeight: filter === f.value ? 600 : 400,
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: colors.errorLight, color: colors.error, border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md }}>
          {error}
        </div>
      )}

      {upgrade ? (
        <div style={emptyCard}>
          <div style={{ fontSize: fontSize.lg, marginBottom: spacing.sm, color: colors.text }}>E-signatures are an Ultimate plan feature</div>
          <div style={{ fontSize: fontSize.base, marginBottom: spacing.md }}>Upgrade to send rendered documents for legally binding electronic signature.</div>
          <Link to="/app/billing" style={{ color: colors.primary, fontWeight: 600 }}>View plans</Link>
        </div>
      ) : loading ? (
        <div style={{ color: colors.textSecondary }}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={emptyCard}>
          <div style={{ fontSize: fontSize.lg, marginBottom: spacing.sm }}>No signing requests</div>
          <div style={{ fontSize: fontSize.base }}>
            {filter ? 'No requests match this filter.' : <>Send a PDF for signature from <Link to="/app/render-jobs" style={{ color: colors.primary }}>Render Jobs</Link>.</>}
          </div>
        </div>
      ) : (
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.sm, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Title</th>
                <th style={th}>Status</th>
                <th style={th}>Signed</th>
                {!isMobile && <th style={th}>Created</th>}
                {!isMobile && <th style={th}>Expires</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.esignRequestId} onClick={() => setSelectedId(r.esignRequestId)}
                  style={{ cursor: 'pointer', background: selectedId === r.esignRequestId ? colors.primaryLight : undefined }}>
                  <td style={{ ...td, fontWeight: 500 }}>{r.title || <span style={{ color: colors.textMuted }}>Untitled</span>}</td>
                  <td style={td}><StatusBadge status={r.status} map={REQUEST_STATUS_CHIP} /></td>
                  <td style={{ ...td, color: colors.textSecondary }}>{r.signedCount}/{r.signerCount}</td>
                  {!isMobile && <td style={{ ...td, color: colors.textSecondary, whiteSpace: 'nowrap' }}>{fmtDate(r.createdAt)}</td>}
                  {!isMobile && <td style={{ ...td, color: colors.textSecondary, whiteSpace: 'nowrap' }}>{r.status === 'pending' ? fmtDate(r.expiresAt) : '—'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && (
        <RequestDrawer
          esignRequestId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => load(filter)}
        />
      )}
    </div>
  )
}

const emptyCard: React.CSSProperties = {
  background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md,
  padding: spacing.xxl, textAlign: 'center', color: colors.textSecondary, boxShadow: shadow.sm,
}

function RequestDrawer({ esignRequestId, onClose, onChanged }: { esignRequestId: string; onClose: () => void; onChanged: () => void }) {
  const [detail, setDetail] = useState<ESignStatus | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState<'remind' | 'void' | 'download' | null>(null)
  const [voidOpen, setVoidOpen] = useState(false)
  const [voidReason, setVoidReason] = useState('')

  const load = useCallback(async () => {
    try {
      setDetail(await esignApiService.status(esignRequestId))
    } catch (err) {
      setError(errorMessage(err, 'Failed to load this request.'))
    }
  }, [esignRequestId])

  useEffect(() => { setDetail(null); setError(''); setNotice(''); load() }, [load])

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !voidOpen) onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [onClose, voidOpen])

  async function remind() {
    setBusy('remind'); setError(''); setNotice('')
    try {
      const { reminded } = await esignApiService.remind(esignRequestId)
      setNotice(reminded > 0 ? `Reminder sent to ${reminded} signer${reminded === 1 ? '' : 's'}.` : 'No signers are waiting on a reminder right now.')
      await load()
    } catch (err) {
      setError(errorMessage(err, 'Failed to send reminders.'))
    } finally {
      setBusy(null)
    }
  }

  async function confirmVoid() {
    setBusy('void'); setError(''); setNotice('')
    try {
      await esignApiService.void_(esignRequestId, voidReason.trim() || undefined)
      setVoidOpen(false)
      setNotice('Request voided.')
      await load()
      onChanged()
    } catch (err) {
      setVoidOpen(false)
      setError(err instanceof ApiError && err.status === 403 && !isPlanUpgradeError(err)
        ? 'Only a team admin can void a signing request.'
        : errorMessage(err, 'Failed to void the request.'))
    } finally {
      setBusy(null)
    }
  }

  async function download() {
    setBusy('download'); setError('')
    try {
      const blob = await esignApiService.download(esignRequestId)
      downloadBlob(blob, `${(detail?.title || 'signed-document').replace(/[^\w.-]+/g, '_')}-signed.pdf`)
    } catch (err) {
      setError(errorMessage(err, 'The signed document is not available yet.'))
    } finally {
      setBusy(null)
    }
  }

  const events = detail ? [...detail.events].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()) : []

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: colors.overlay, zIndex: 900 }} />
      <aside role="dialog" aria-label="Signing request details" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(480px, 100vw)', zIndex: 901,
        background: colors.surface, boxShadow: shadow.xl, display: 'flex', flexDirection: 'column',
        borderLeft: `1px solid ${colors.border}`, boxSizing: 'border-box',
      }}>
        <div style={{ padding: `${spacing.md} ${spacing.lg}`, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'flex-start', gap: spacing.sm }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: fontSize.lg, fontWeight: 600, color: colors.text, overflowWrap: 'anywhere' }}>
              {detail ? detail.title || 'Untitled' : 'Loading…'}
            </div>
            {detail && (
              <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', marginTop: 4, flexWrap: 'wrap', fontSize: fontSize.xs, color: colors.textMuted }}>
                <StatusBadge status={detail.status} map={REQUEST_STATUS_CHIP} />
                <span>Created {fmtDate(detail.createdAt)}</span>
                {detail.status === 'pending' && detail.expiresAt && <span>· Expires {fmtDate(detail.expiresAt)}</span>}
                {detail.completedAt && <span>· Completed {fmtDate(detail.completedAt)}</span>}
                {detail.voidedAt && <span>· Voided {fmtDate(detail.voidedAt)}</span>}
                {detail.declinedAt && <span>· Declined {fmtDate(detail.declinedAt)}</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 20, color: colors.textMuted, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: `${spacing.md} ${spacing.lg}` }}>
          {error && <div role="alert" style={{ background: colors.errorLight, color: colors.error, border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm, padding: spacing.sm, fontSize: fontSize.sm, marginBottom: spacing.md }}>{error}</div>}
          {notice && <div style={{ background: colors.successLight, color: colors.success, border: `1px solid ${colors.successBorder}`, borderRadius: radius.sm, padding: spacing.sm, fontSize: fontSize.sm, marginBottom: spacing.md }}>{notice}</div>}

          {detail && (
            <>
              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.lg }}>
                {detail.status === 'pending' && (
                  <>
                    <Button size="sm" variant="secondary" onClick={remind} loading={busy === 'remind'}>Remind</Button>
                    <Button size="sm" variant="danger" onClick={() => { setVoidReason(''); setVoidOpen(true) }}>Void</Button>
                  </>
                )}
                {detail.signedDocumentAvailable && (
                  <Button size="sm" onClick={download} loading={busy === 'download'}>Download signed PDF</Button>
                )}
              </div>

              {detail.message && (
                <p style={{ fontSize: fontSize.sm, color: colors.textBody, whiteSpace: 'pre-wrap', margin: `0 0 ${spacing.lg}` }}>{detail.message}</p>
              )}

              <SectionLabel>Signers{detail.sequential ? ' (in order)' : ''}</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, marginBottom: spacing.lg }}>
                {[...detail.signers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(s => (
                  <div key={s.esignSignerId} data-testid="signer" style={{ border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: spacing.sm }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: spacing.sm, alignItems: 'center' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: fontSize.sm, fontWeight: 500, color: colors.text, overflowWrap: 'anywhere' }}>
                          {detail.sequential && s.order != null ? `${s.order}. ` : ''}{s.name || s.email}
                        </div>
                        {s.name && <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, overflowWrap: 'anywhere' }}>{s.email}</div>}
                      </div>
                      <StatusBadge status={s.status} map={SIGNER_STATUS_CHIP} />
                    </div>
                    <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: 4 }}>
                      {[
                        s.viewedAt && `Viewed ${fmtDate(s.viewedAt, true)}`,
                        s.signedAt && `Signed ${fmtDate(s.signedAt, true)}`,
                        s.declinedAt && `Declined ${fmtDate(s.declinedAt, true)}`,
                        s.lastRemindedAt && `Reminded ${fmtDate(s.lastRemindedAt, true)}`,
                      ].filter(Boolean).join(' · ')}
                    </div>
                    {s.declineReason && <div style={{ fontSize: fontSize.xs, color: colors.error, marginTop: 4 }}>“{s.declineReason}”</div>}
                  </div>
                ))}
              </div>

              <SectionLabel>Activity</SectionLabel>
              {events.length === 0 ? (
                <div style={{ fontSize: fontSize.sm, color: colors.textMuted }}>No activity yet.</div>
              ) : (
                <ol style={{ listStyle: 'none', margin: 0, padding: 0, borderLeft: `2px solid ${colors.border}` }}>
                  {events.map((e, i) => (
                    <li key={i} style={{ position: 'relative', padding: `0 0 ${spacing.md} ${spacing.md}` }}>
                      <span style={{ position: 'absolute', left: -6, top: 4, width: 10, height: 10, borderRadius: '50%', background: colors.primary }} />
                      <div style={{ fontSize: fontSize.sm, color: colors.text }}>
                        {eventLabel(e.eventType)}{e.signerEmail ? ` · ${e.signerEmail}` : ''}
                      </div>
                      <div style={{ fontSize: fontSize.xs, color: colors.textMuted }}>
                        {fmtDate(e.at, true)}{e.ipAddress && <span style={{ fontFamily: fontFamily.mono }}> · {e.ipAddress}</span>}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </div>
      </aside>

      <ConfirmModal
        open={voidOpen}
        title="Void this request?"
        danger
        confirmLabel="Void request"
        loading={busy === 'void'}
        onCancel={() => setVoidOpen(false)}
        onConfirm={confirmVoid}
        message={
          <>
            <p style={{ margin: `0 0 ${spacing.md}` }}>Signers can no longer open or sign this document. This cannot be undone.</p>
            <Textarea label="Reason (optional)" aria-label="Void reason" value={voidReason} onChange={e => setVoidReason(e.target.value)} rows={3} maxLength={500} />
          </>
        }
      />
    </>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: fontSize.xs, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: spacing.sm }}>{children}</div>
}
